import { useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { m } from "framer-motion";
import { ArrowRight, Check, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { submitEnquiry } from "@/lib/enquiries.functions";
import { trackEvent } from "@/lib/analytics";
import { CalendlyInlineWidget } from "@/components/site/CalendlyInlineWidget";
import { BookingIntro } from "@/components/site/BookingIntro";
import { PhoneCallLink } from "@/components/site/PhoneCallLink";


export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Contact Ajility: Book a 30-Minute Fit Call" },
      {
        name: "description",
        content:
          "Book a free 30-minute fit call with Ajility. Fractional COO support for founder-led businesses in Sydney, Melbourne and Perth.",
      },
      { property: "og:title", content: "Contact Ajility: Book a 30-Minute Fit Call" },
      {
        property: "og:description",
        content:
          "Book a free 30-minute fit call with Ajility. Fractional COO support for founder-led businesses in Sydney, Melbourne and Perth.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Contact Ajility: Book a 30-Minute Fit Call" },
      { name: "twitter:description", content: "Book a free 30-minute fit call with Ajility. Fractional COO support for founder-led businesses in Sydney, Melbourne and Perth." },
      ...ogImage("contact", "Book a 30-minute fit call with Ajility"),
      ogUrl("/contact"),
    ],
    links: canonicalLinks("/contact"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Contact", path: "/contact" }])),
      jsonLd(webPageSchema({ image: "contact",
        path: "/contact",
        name: "Contact Ajility: Book a 30-Minute Fit Call",
        description: "Book a free 30-minute fit call with Ajility. Fractional COO support for founder-led businesses in Sydney, Melbourne and Perth.",
        type: "ContactPage",
      })),
    ],
  }),
  component: ContactPage,
});

const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  phone: "Mobile",
  email: "Email",
  company: "Company",
  revenue: "Revenue range",
  bestTime: "Best time to call",
  message: "What operational challenge are you facing?",
};

function describeError(err: unknown): string {
  const fallback = "Please try again or email us directly.";
  if (!(err instanceof Error)) return fallback;
  try {
    const issues = JSON.parse(err.message) as Array<{ path?: unknown[]; message?: string }>;
    if (!Array.isArray(issues) || issues.length === 0) return fallback;
    const lines = issues
      .map((issue) => {
        const key = String(issue.path?.[0] ?? "");
        const label = FIELD_LABELS[key] ?? key;
        const text = issue.message ?? "is invalid";
        return label ? `${label}: ${text}` : text;
      })
      .filter(Boolean);
    return lines.length > 0 ? lines.join(". ") : fallback;
  } catch {
    return err.message || fallback;
  }
}

function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const startedAt = useRef<number>(Date.now());

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setSubmitting(true);
    try {
      await submitEnquiry({
        data: {
          name: String(fd.get("name") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          company: String(fd.get("company") ?? ""),
          email: String(fd.get("email") ?? ""),
          revenue: String(fd.get("revenue") ?? "") as "Under $3M",
          bestTime: String(fd.get("bestTime") ?? "") as "",
          message: String(fd.get("message") ?? ""),
          website: String(fd.get("website") ?? ""),
          elapsedMs: Date.now() - startedAt.current,
        },
      });
      form.reset();
      startedAt.current = Date.now();
      trackEvent("contact_submit", { form: "contact" });
      toast.success("Thanks. Adam will call you within one business day.", {
        description: `Confirmation sent to ${fd.get("email")}`,
      });
    } catch (err) {
      toast.error("Something went wrong sending your enquiry.", {
        description: describeError(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <section className="bg-midnight pb-16 pt-32 text-frost">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <m.p
              initial={false}
              animate={{ y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-sm font-semibold uppercase tracking-widest text-silver"
            >
              Contact
            </m.p>
            <m.h1
              initial={false}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl"
            >
               Contact Ajility for fractional COO support
            </m.h1>
            <m.p
              initial={false}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-xl leading-relaxed text-frost/80"
            >
              Three ways to start. Book a 30 minute fit call or run the six minute Operating Scan. Or ring Adam on{" "}
              <a href="tel:+61423335555" onClick={() => trackEvent("phone_click", { location: "contact_hero" })} className="underline underline-offset-4 hover:text-frost">0423 335 555</a>. If you would rather send a note first, the form is below.
            </m.p>
          </div>
        </div>
      </section>

      <section className="bg-surface pt-16 lg:pt-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div id="book-fit-call" className="scroll-mt-28 rounded-3xl bg-midnight p-8 text-frost shadow-elevated">
              <h2 className="font-serif text-2xl md:text-3xl">Book a 30 minute fit call</h2>
              <BookingIntro>
                <p className="mt-5 text-frost/70">
                  No sales team, no qualifier call. A straight answer on fit either way.
                </p>
              </BookingIntro>
              <CalendlyInlineWidget location="contact_page" />
              <PhoneCallLink placement="contact_card" className="mt-4 inline-block text-sm font-medium text-frost/70 hover:text-frost" />
            </div>

            <div className="flex flex-col self-start rounded-3xl border border-border bg-card p-8 shadow-elevated">
              <h2 className="font-serif text-2xl text-midnight md:text-3xl">
                Run the six minute Operating Scan
              </h2>
              <p className="mt-4 text-charcoal">
                21 statements, your score across six operating areas, and the weakest area named. Free, and you see the score before you give any details.
              </p>
              <a
                href="https://diagnostic.ajility.com.au/?start=scan&utm_source=ajility_site&utm_medium=referral&utm_campaign=contact_page"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("scan_start_click", { location: "contact_page" })}
                className="mt-6 inline-flex h-12 w-fit items-center rounded-full bg-midnight px-7 text-base font-semibold text-frost hover:bg-midnight-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                Start the scan
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <ul className="mt-6 space-y-3">
                {["Free", "Six minutes", "Score shown immediately"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-charcoal">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-steel" />
                    {t}
                  </li>
                ))}
              </ul>
              <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-6">
                {["Direction", "Leadership", "People", "Rhythm", "Numbers", "Systems"].map((area) => <li key={area} className="flex min-w-0 items-center gap-2 text-sm text-charcoal"><Check className="h-4 w-4 shrink-0 text-steel" aria-hidden="true" />{area}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
                <h2 className="font-serif text-xl text-midnight md:text-2xl">
                  Prefer to send a note first?
                </h2>
                <p className="mt-2 text-charcoal">
                  We'll respond within one business day.
                </p>
                <form onSubmit={handleSubmit} className="ph-no-capture mt-8 space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field id="name" label="Name" required placeholder="Your name" autoComplete="name" />
                    <Field id="phone" label="Mobile" type="tel" required placeholder="04xx xxx xxx" autoComplete="tel" />
                  </div>
                  <Field
                    id="email"
                    label="Email"
                    type="email"
                    required
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                  <Field id="company" label="Company" placeholder="Business name" autoComplete="organization" />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <SelectField
                      id="revenue"
                      label="Revenue range"
                      required
                      options={["Under $3M", "$3M to $12M", "$12M to $30M", "Over $30M"]}
                    />
                    <SelectField
                      id="bestTime"
                      label="Best time to call"
                      options={["Morning", "Afternoon", "Any time"]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">
                      What operational challenge are you facing? <span className="text-steel">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      maxLength={2000}
                      placeholder="Describe the friction, growth ceiling or team issue..."
                      className="rounded-2xl border-field-border bg-surface"
                    />
                  </div>
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="contact-website">Website</label>
                    <input
                      id="contact-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    size="lg"
                    className="mt-2 h-12 rounded-full bg-midnight text-base font-semibold text-frost hover:bg-midnight-deep"
                  >
                    {submitting ? "Sending…" : "Send enquiry"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-sm text-charcoal">
                    Want a faster answer?{" "}
                    <a href="#book-fit-call" className="underline underline-offset-2 hover:text-midnight">
                      Book the fit call
                    </a>{" "}
                    above.
                  </p>
                  <p className="text-xs text-charcoal">
                    By submitting you agree to be contacted about your enquiry. See our{" "}
                    <Link to="/privacy" className="underline underline-offset-2 hover:text-midnight">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </form>
              </div>
            </div>

            <div className="space-y-6 lg:col-span-5">
              <div className="rounded-3xl border border-border bg-card p-8">
                <h3 className="font-serif text-2xl text-foreground">Direct contact</h3>
                <ul className="mt-5 space-y-4 text-sm text-charcoal">
                  <li className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-steel" />
                    <a href="tel:+61423335555" onClick={() => trackEvent("phone_click", { location: "contact_direct" })} className="hover:text-foreground">
                      0423 335 555
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-steel" />
                    <a href="mailto:adam@ajility.com.au" className="hover:text-foreground">
                      adam@ajility.com.au
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Linkedin className="h-4 w-4 text-steel" />
                    <a
                      href="https://www.linkedin.com/in/adamayoub/"
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-foreground"
                    >
                      LinkedIn, Adam Ayoub
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 text-steel" />
                    <span>
                      Sydney, Melbourne, Newcastle, Central Coast, Perth
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-3xl border border-border bg-card p-8">
                <h3 className="font-serif text-2xl text-foreground">
                  Ideal client snapshot
                </h3>
                <ul className="mt-5 space-y-3">
                  {[
                    "Founder-led, $3M to $30M revenue",
                    "15 to 150 employees",
                    "Growth outpacing structure",
                    "Open to embedded operational leadership",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-charcoal">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-steel" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({
  id,
  label,
  type = "text",
  required,
  placeholder,
  autoComplete,
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-steel">*</span>}
      </Label>
      <Input
        id={id}
        name={id}
        type={type}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="h-12 rounded-2xl border-field-border bg-surface"
      />
    </div>
  );
}

function SelectField({
  id,
  label,
  options,
  required,
}: {
  id: string;
  label: string;
  options: string[];
  required?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-steel">*</span>}
      </Label>
      <select
        id={id}
        name={id}
        required={required}
        defaultValue=""
        className="h-12 w-full rounded-2xl border border-field-border bg-surface px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="" disabled={required}>
          {required ? "Select" : "No preference"}
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
